import { Document, Page, pdf, StyleSheet, Text, View } from '@react-pdf/renderer';
import type { Style } from '@react-pdf/types';
import { ReactElement } from 'react';
import { CellColors, DAY_RESULT_FIELDS, DIGITS, EXTRA_TITLES, SheetExtras } from './calculatorShared';

// Printable versions of the Single and Patti sheets, laid out like the paper sheet

const BORDER = '#888';

const s = StyleSheet.create({
    page: { padding: 20, fontSize: 9, fontFamily: 'Helvetica' },
    title: { fontSize: 14, fontFamily: 'Helvetica-Bold', textAlign: 'center' },
    subtitle: { fontSize: 9, textAlign: 'center', color: '#444', marginTop: 2, marginBottom: 8 },
    table: { borderTopWidth: 1, borderLeftWidth: 1, borderColor: BORDER },
    row: { flexDirection: 'row' },
    cell: {
        flex: 1,
        borderRightWidth: 1,
        borderBottomWidth: 1,
        borderColor: BORDER,
        paddingVertical: 3,
        paddingHorizontal: 2,
        justifyContent: 'center',
    },
    nameCell: { flex: 2.2 },
    text: { textAlign: 'center' },
    nameText: { textAlign: 'left' },
    bold: { fontFamily: 'Helvetica-Bold' },
    head: { backgroundColor: '#eeeeee' },
    total: { backgroundColor: '#fffbe6' },
    pattiCell: { paddingVertical: 1 },
    pattiLabel: { fontSize: 7, color: '#666', textAlign: 'center' },
    extras: { marginTop: 12 },
    extrasRow: { flexDirection: 'row', marginBottom: 8 },
    box: { borderWidth: 1, borderColor: BORDER, padding: 6, minHeight: 60 },
    rowBox: { flex: 1 },
    boxGap: { marginRight: 8 },
    boxTitle: { fontFamily: 'Helvetica-Bold', marginBottom: 4 },
    hisab: { minHeight: 90 },
});

// react-pdf takes a style object or an array of them
type PdfStyle = Style | Style[];

const toList = (style?: PdfStyle) => (Array.isArray(style) ? style : style ? [style] : []);

const PdfCell = ({ children, style, textStyle, bg }: {
    children?: React.ReactNode;
    style?: PdfStyle;
    textStyle?: PdfStyle;
    bg?: string;
}) => (
    <View style={[s.cell, ...toList(style), ...(bg ? [{ backgroundColor: bg }] : [])]}>
        {children !== undefined && <Text style={textStyle ?? s.text}>{children}</Text>}
    </View>
);

const ExtrasPdf = ({ extras }: { extras: SheetExtras }) => (
    <View style={s.extras} wrap={false}>
        <View style={s.extrasRow}>
            <View style={[s.box, s.rowBox, s.boxGap]}>
                <Text style={s.boxTitle}>{EXTRA_TITLES.dayResults}</Text>
                {DAY_RESULT_FIELDS.map(({ field, label }) => (
                    <Text key={field}>{label}: {extras[field]}</Text>
                ))}
            </View>
            <View style={[s.box, s.rowBox, s.boxGap]}>
                <Text style={s.boxTitle}>{EXTRA_TITLES.todayResult}</Text>
                <Text style={{ fontSize: 14, fontFamily: 'Helvetica-Bold' }}>{extras.todayResult}</Text>
            </View>
            <View style={[s.box, s.rowBox, s.boxGap]}>
                <Text style={s.boxTitle}>{EXTRA_TITLES.band}</Text>
                <Text>{extras.band}</Text>
            </View>
            <View style={[s.box, s.rowBox]}>
                <Text style={s.boxTitle}>{EXTRA_TITLES.bandAajKhula}</Text>
                <Text>{extras.bandAajKhula}</Text>
            </View>
        </View>
        <View style={[s.box, s.hisab]}>
            <Text style={s.boxTitle}>{EXTRA_TITLES.hisab}</Text>
            <Text>{extras.hisab}</Text>
        </View>
    </View>
);

interface SinglePdfProps {
    subtitle: string;
    clients: Array<{ clientName: string; total: number }>;
    pointsOf: (clientName: string, digit: string) => string;
    digitTotals: Record<string, number>;
    total: number;
    colors: CellColors;
    cellKey: (clientName: string, digit: string) => string;
    extras: SheetExtras;
}

export const SinglePdf = ({ subtitle, clients, pointsOf, digitTotals, total, colors, cellKey, extras }: SinglePdfProps) => {
    const digitRow = (
        <View style={s.row}>
            <PdfCell style={[s.nameCell, s.head]} />
            {DIGITS.map(d => <PdfCell key={d} style={s.head} textStyle={[s.text, s.bold]}>{d}</PdfCell>)}
            <PdfCell style={[s.nameCell, s.head]} />
            <PdfCell style={s.head} />
        </View>
    );
    return (
        <Document title={`Single ${subtitle}`}>
            <Page size="A4" orientation="landscape" style={s.page}>
                <Text style={s.title}>Single</Text>
                <Text style={s.subtitle}>{subtitle}</Text>
                <View style={s.table}>
                    <View style={s.row} fixed>
                        <PdfCell style={[s.nameCell, s.head]} textStyle={[s.nameText, s.bold]}>NAME</PdfCell>
                        {DIGITS.map(d => <PdfCell key={d} style={s.head} textStyle={[s.text, s.bold]}>{d}</PdfCell>)}
                        <PdfCell style={[s.nameCell, s.head]} textStyle={[s.nameText, s.bold]}>NAME</PdfCell>
                        <PdfCell style={s.head} textStyle={[s.text, s.bold]}>TOTAL</PdfCell>
                    </View>
                    {clients.map(c => (
                        <View key={c.clientName} style={s.row} wrap={false}>
                            <PdfCell style={[s.nameCell, s.head]} textStyle={[s.nameText, s.bold]}>{c.clientName}</PdfCell>
                            {DIGITS.map(d => (
                                <PdfCell key={d} bg={colors[cellKey(c.clientName, d)]}>{pointsOf(c.clientName, d)}</PdfCell>
                            ))}
                            <PdfCell style={[s.nameCell, s.head]} textStyle={[s.nameText, s.bold]}>{c.clientName}</PdfCell>
                            <PdfCell style={s.total} textStyle={[s.text, s.bold]}>{c.total}</PdfCell>
                        </View>
                    ))}
                    <View style={s.row} wrap={false}>
                        <PdfCell style={[s.nameCell, s.head]} textStyle={[s.nameText, s.bold]}>TOTAL</PdfCell>
                        {DIGITS.map(d => <PdfCell key={d} style={s.total} textStyle={[s.text, s.bold]}>{digitTotals[d]}</PdfCell>)}
                        <PdfCell style={[s.nameCell, s.head]} textStyle={[s.nameText, s.bold]}>TOTAL</PdfCell>
                        <PdfCell style={s.total} textStyle={[s.text, s.bold]}>{total}</PdfCell>
                    </View>
                    {digitRow}
                </View>
                <ExtrasPdf extras={extras} />
            </Page>
        </Document>
    );
};

interface PattiPdfProps {
    subtitle: string;
    columns: string[][];
    rowCount: number;
    pointsOf: (patti: string) => string;
    columnTotals: number[];
    total: number;
    colors: CellColors;
    extras: SheetExtras;
}

export const PattiPdf = ({ subtitle, columns, rowCount, pointsOf, columnTotals, total, colors, extras }: PattiPdfProps) => {
    const digitRow = (
        <View style={s.row}>
            {DIGITS.map(d => <PdfCell key={d} style={s.head} textStyle={[s.text, s.bold]}>{d}</PdfCell>)}
        </View>
    );
    return (
        <Document title={`Patti ${subtitle}`}>
            <Page size="A4" style={s.page}>
                <Text style={s.title}>Patti</Text>
                <Text style={s.subtitle}>{subtitle}</Text>
                <View style={s.table}>
                    {digitRow}
                    {Array.from({ length: rowCount }, (_, rowIndex) => (
                        <View key={rowIndex} style={s.row} wrap={false}>
                            {columns.map((column, colIndex) => {
                                const patti = column[rowIndex];
                                return (
                                    <View key={colIndex} style={[s.cell, s.pattiCell, ...(patti && colors[patti] ? [{ backgroundColor: colors[patti] }] : [])]}>
                                        {patti && (
                                            <>
                                                <Text style={s.pattiLabel}>{patti}</Text>
                                                <Text style={[s.text, s.bold]}>{pointsOf(patti) || ' '}</Text>
                                            </>
                                        )}
                                    </View>
                                );
                            })}
                        </View>
                    ))}
                    <View style={s.row}>
                        {columnTotals.map((t, i) => <PdfCell key={i} style={s.total} textStyle={[s.text, s.bold]}>{t}</PdfCell>)}
                    </View>
                    {digitRow}
                    <View style={s.row}>
                        <PdfCell style={s.total} textStyle={[s.text, s.bold]}>TOTAL: {total}</PdfCell>
                    </View>
                </View>
                <ExtrasPdf extras={extras} />
            </Page>
        </Document>
    );
};

export const downloadPdf = async (doc: ReactElement, fileName: string) => {
    const blob = await pdf(doc as any).toBlob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    // Give the browser a moment to start the download before freeing the blob
    setTimeout(() => URL.revokeObjectURL(url), 1000);
};
